'use client';

/**
 * KanbanBoard Component
 *
 * A full-featured Kanban board with drag-and-drop support using @dnd-kit.
 * Supports multiple columns, WIP limits, SLA indicators, and neon accent styling.
 */

import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import type { KanbanBoardProps, KanbanDragResult, KanbanItem } from '../../types/pipeline';
import { LoadingOverlay } from '../LoadingOverlay';
import { KanbanColumn } from './KanbanColumn';

// ============================================================================
// Styled Components
// ============================================================================

interface StyledBoardProps {
  $isDark: boolean;
}

const StyledBoard = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledBoardProps>(({ $isDark }) => ({
  display: 'flex',
  gap: '16px',
  padding: '16px',
  minHeight: '500px',
  height: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',

  // Custom scrollbar
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: $isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: $isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: $isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
}));

const BoardContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
});

// Drag overlay card styles
const DragOverlayCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: '12px',
    borderRadius: '12px',
    background: glass.backgroundHover,
    border: `1px solid ${glass.borderHover}`,
    boxShadow: `${glass.shadowHover}, 0 20px 40px rgba(0,0,0,0.3)`,
    cursor: 'grabbing',
    transform: 'rotate(3deg) scale(1.02)',
    opacity: 0.95,
  };
});

// ============================================================================
// Component
// ============================================================================

export function KanbanBoard<T>({
  columns,
  items,
  renderCard,
  onCardClick,
  onDragEnd,
  loading = false,
  neonAccent,
  showSLA = true,
  showCounts = true,
  allowAdd = false,
  onAddCard,
}: KanbanBoardProps<T>) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Track active drag item for overlay rendering
  const [activeItem, setActiveItem] = useState<KanbanItem<T> | null>(null);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum distance before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Group items by column
  const itemsByColumn = useMemo(() => {
    const grouped: Record<string, KanbanItem<T>[]> = {};

    columns.forEach((col) => {
      grouped[col.id] = [];
    });

    items.forEach((item) => {
      if (grouped[item.columnId]) {
        grouped[item.columnId].push(item);
      }
    });

    // Sort by order within each column
    Object.keys(grouped).forEach((colId) => {
      grouped[colId].sort((a, b) => a.order - b.order);
    });

    return grouped;
  }, [columns, items]);

  // Find item by ID
  const findItem = useCallback(
    (id: UniqueIdentifier): KanbanItem<T> | undefined => {
      return items.find((item) => item.id === id);
    },
    [items],
  );

  // Drag start handler
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;

      const item = findItem(active.id);
      if (item) {
        setActiveItem(item);
      }
    },
    [findItem],
  );

  // Drag over handler (for column changes during drag)
  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // Visual feedback during drag could be added here
    // For now, the actual move is handled in onDragEnd
  }, []);

  // Drag end handler
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveItem(null);

      if (!over) return;

      const activeItem = findItem(active.id);
      if (!activeItem) return;

      // Determine target column
      let toColumnId: string;
      let toIndex: number;

      if (over.data.current?.['type'] === 'column') {
        // Dropped on a column (not on a specific card)
        toColumnId = over.id as string;
        toIndex = itemsByColumn[toColumnId]?.length || 0;
      } else {
        // Dropped on a card
        const overItem = findItem(over.id);
        if (!overItem) return;

        toColumnId = overItem.columnId;
        const columnItems = itemsByColumn[toColumnId] || [];
        toIndex = columnItems.findIndex((item) => item.id === over.id);

        // If moving within the same column, adjust index for removal
        if (activeItem.columnId === toColumnId) {
          const activeIndex = columnItems.findIndex((item) => item.id === active.id);
          if (activeIndex < toIndex) {
            toIndex = toIndex - 1;
          }
        }
      }

      const fromColumnId = activeItem.columnId;
      const fromIndex = itemsByColumn[fromColumnId]?.findIndex((item) => item.id === active.id) || 0;

      // Only trigger callback if something changed
      if (fromColumnId !== toColumnId || fromIndex !== toIndex) {
        const result: KanbanDragResult = {
          itemId: activeItem.id,
          fromColumnId,
          toColumnId,
          fromIndex,
          toIndex: Math.max(0, toIndex),
        };

        onDragEnd(result);
      }
    },
    [findItem, itemsByColumn, onDragEnd],
  );

  // Drag cancel handler
  const handleDragCancel = useCallback(() => {
    setActiveItem(null);
  }, []);

  return (
    <BoardContainer>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <StyledBoard $isDark={isDark}>
          {columns.map((column) => (
            <KanbanColumn
              key={column.id}
              column={column}
              items={itemsByColumn[column.id] || []}
              renderCard={renderCard}
              onCardClick={onCardClick}
              neonAccent={column.color || neonAccent}
              showSLA={showSLA}
              showCount={showCounts}
              allowAdd={allowAdd}
              onAddCard={onAddCard ? () => onAddCard(column.id) : undefined}
            />
          ))}
        </StyledBoard>

        {/* Drag Overlay - shows a preview of the dragged item */}
        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            <DragOverlayCard
              $isDark={isDark}
              initial={{ scale: 1, rotate: 0 }}
              animate={{ scale: 1.02, rotate: 3 }}
              transition={{ duration: 0.15 }}
            >
              {renderCard(activeItem)}
            </DragOverlayCard>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Loading Overlay */}
      <LoadingOverlay visible={!!loading} borderRadius="16px" />
    </BoardContainer>
  );
}

KanbanBoard.displayName = 'KanbanBoard';

export default KanbanBoard;
